import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import SiteGate, { GATE_STORAGE_KEY } from "@/pages/SiteGate";

/**
 * Site-wide splash gate.
 *
 * BEHAVIOUR
 *  • If the visitor is NOT signed in AND has not passed the gate before,
 *    the SiteGate splash page is rendered in place of the entire app.
 *  • Once a valid access code is entered (or the visitor signs in) the flag
 *    is stored in localStorage and the real app is shown.
 *
 * PUBLIC LAUNCH OVERRIDE
 *  When the admin flips `landing_mode = "public"` from the Admin console the
 *  temporary SiteGate splash is bypassed entirely — visitors go directly to
 *  the real Hunt57 landing page (`/`) with its SIGN UP / SIGN IN buttons.
 *  Toggle back to `invite_only` at any time to re-enable the gate.
 */
export default function GateGuard({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [passed, setPassed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(GATE_STORAGE_KEY) === "1"
  );
  // `null` while we resolve the site config; falls back to gate-on if the
  // fetch errors so we never accidentally leak an early-launch state.
  const [landingMode, setLandingMode] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get("/site-config")
      .then((r) => { if (mounted) setLandingMode(r.data?.landing_mode || "invite_only"); })
      .catch(() => { if (mounted) setLandingMode("invite_only"); });
    return () => { mounted = false; };
  }, []);

  // If a signed-in user reaches the app, treat the gate as passed.
  useEffect(() => {
    if (user && !passed) {
      localStorage.setItem(GATE_STORAGE_KEY, "1");
      setPassed(true);
    }
  }, [user, passed]);

  // Keep sync across tabs.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === GATE_STORAGE_KEY) {
        setPassed(e.newValue === "1");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Force-show the splash when ?preview_gate=1 is in the URL (used from Admin → Access).
  const forcedPreview = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("preview_gate") === "1";

  // Paths that must render publicly regardless of the invite-only gate.
  // The QR-code contest landing page MUST be reachable off-platform.
  const ALWAYS_PUBLIC = new Set([
    "/contest",
  ]);
  if (!forcedPreview && ALWAYS_PUBLIC.has(location.pathname)) return children;

  // Still resolving auth or site-config — render nothing to avoid flashing.
  if (loading || landingMode === null) return null;

  // Public-launch override — bypass the gate entirely unless admin is
  // explicitly previewing via ?preview_gate=1.
  if (!forcedPreview && landingMode === "public") return children;

  if (!forcedPreview && (passed || user)) return children;

  return <SiteGate onPass={() => setPassed(true)} key={location.pathname} />;
}
